import { Braces, FileCode2, Hexagon, LayoutTemplate, ShoppingBag, type LucideIcon } from "lucide-react";
import { Badge, type BadgeTone } from "../badge";
import { IconBadge, type SoftTone } from "../metrics";
import { TechLogo, type Tech } from "../tech-logo";

const WEBSITE_TONE: Record<string, BadgeTone> = {
  ACTIVE: "success", PROVISIONING: "info", PENDING: "neutral", SUSPENDED: "warning", FAILED: "danger", DELETING: "warning", DELETED: "neutral",
};
export function WebsiteStatusBadge({ status }: { status: string }) {
  return <Badge tone={WEBSITE_TONE[status] ?? "neutral"} dot>{status.charAt(0) + status.slice(1).toLowerCase()}</Badge>;
}

const SSL_TONE: Record<string, BadgeTone> = { ISSUED: "success", PENDING: "info", EXPIRING: "warning", EXPIRED: "danger", FAILED: "danger", NONE: "neutral" };
const SSL_LABEL: Record<string, string> = { ISSUED: "Secure", PENDING: "Issuing", EXPIRING: "Expiring", EXPIRED: "Expired", FAILED: "Failed", NONE: "Not yet" };
export function SslBadge({ status }: { status: string }) {
  return <Badge tone={SSL_TONE[status] ?? "neutral"}>{SSL_LABEL[status] ?? status}</Badge>;
}

export const runtimeLabel = (rt: string, v: string) => `${rt === "php" ? "PHP" : rt === "node" ? "Node.js" : "Python"} ${v}`;

/**
 * PHP versions past their upstream support (php.net/supported-versions). They are offered so migrated sites can keep
 * the version they run today, and are labelled so nobody picks them for a new site by accident.
 */
const PHP_SUPPORT: Record<string, { short: string; long: string }> = {
  "7.4": { short: "end of life", long: "No longer receives security fixes (ended Nov 2022). Only for older sites that need it." },
  "8.0": { short: "end of life", long: "No longer receives security fixes (ended Nov 2023). Only for older sites that need it." },
  "8.1": { short: "end of life", long: "No longer receives security fixes (ended Dec 2025). Only for older sites that need it." },
  "8.2": { short: "security fixes only", long: "Security fixes only, until the end of 2026." },
};
export const runtimeSupportNote = (rt: string, v: string) => (rt === "php" ? PHP_SUPPORT[v] ?? null : null);
/** A version for a select list, with its support status when it is past active support. */
export const runtimeOptionLabel = (rt: string, v: string) => {
  const n = runtimeSupportNote(rt, v);
  return `${runtimeLabel(rt, v)}${n ? ` (${n.short})` : ""}`;
};

const APP_META: Record<string, { label: string; icon: LucideIcon; tone: SoftTone; tech: Tech | null }> = {
  WORDPRESS: { label: "WordPress", icon: LayoutTemplate, tone: "info", tech: "wordpress" },
  WOOCOMMERCE: { label: "WooCommerce", icon: ShoppingBag, tone: "accent", tech: "woocommerce" },
  PHP: { label: "PHP application", icon: FileCode2, tone: "info", tech: "php" },
  NODE: { label: "Node.js application", icon: Hexagon, tone: "success", tech: "node" },
  PYTHON: { label: "Python application", icon: Braces, tone: "warning", tech: "python" },
  STATIC: { label: "Static site", icon: FileCode2, tone: "neutral", tech: null },
};
/** Icon, brand logo, human label and soft tone for a website's application type. */
export function appTypeMeta(appType: string, runtime?: string) {
  return APP_META[appType] ?? APP_META[(runtime ?? "").toUpperCase()] ?? { label: "Application", icon: FileCode2, tone: "neutral" as SoftTone, tech: null };
}

/** The application's real logo (WordPress, PHP, Node.js, …), falling back to a generic icon. */
export function AppLogo({ appType, runtime, size = "md", className }: { appType: string; runtime?: string; size?: "xs" | "sm" | "md" | "lg" | "xl"; className?: string }) {
  const m = appTypeMeta(appType, runtime);
  if (m.tech) return <TechLogo tech={m.tech} size={size} className={className} />;
  return <IconBadge icon={m.icon} tone={m.tone} size={size === "xs" || size === "sm" ? "sm" : size === "md" ? "md" : "lg"} className={className} />;
}

const ENGINE_TECH: Record<string, Tech> = { MARIADB: "mariadb", MYSQL: "mysql", POSTGRESQL: "postgresql", MONGODB: "mongodb", REDIS: "redis" };
/** Logo for a database engine. */
export function EngineLogo({ engine, size = "sm", className }: { engine: string; size?: "xs" | "sm" | "md" | "lg"; className?: string }) {
  const t = ENGINE_TECH[engine.toUpperCase()];
  return t ? <TechLogo tech={t} size={size} className={className} /> : null;
}
